import React, { PureComponent } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import BodyClassName from 'react-body-classname';

import { withExperiment } from '@sparefoot/react-core/decorators';

import { LoadingMask } from 'components/core/LoadingMask';
import { Button } from 'components/core/Button';
import { Paragraph, Text } from 'components/core/Typography';
import { Image } from 'components/core/Image';
import { Input } from 'components/core/Input';
import {
	createAutocomplete,
	removeAutocomplete,
	formatPlaceObject,
} from 'utils/search/autocomplete';
import alert from 'assets/images/icons/alert-red.svg';
import MapPin from 'components/svg/MapPin';

import './GoogleAutocomplete.scss';

const mapStateToProps = (state) => ({
	geolocation: state.user.geolocation,
});
export const enhance = compose(
	connect(mapStateToProps),
	withExperiment('sf_15652_geolocation_ssdc'),
);
export class GoogleAutocomplete extends PureComponent {
	constructor(props) {
		super(props);

		this.textInput = null;
		this.autoComplete = null;

		this.state = {
			isFocused: false,
			locating: false,
			errorMessage: false,
		};
	}

	componentDidMount() {
		if (this.props.hideOnScroll) {
			window.addEventListener('scroll', this.handleHideOnScroll);
		}

		this.observer = new MutationObserver(() => {
			const GoogleAutocompleteDrawerEl =
				document.querySelector('.pac-container');
			if (
				GoogleAutocompleteDrawerEl &&
				this.props.sf15652Variation === 'variation1'
			) {
				GoogleAutocompleteDrawerEl.style.transform = 'translateY(28px)';
				this.observer.disconnect();
			}
		});

		this.observer.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}

	componentWillUnmount() {
		if (this.observer) {
			this.observer.disconnect();
		}
		removeAutocomplete(this.autoComplete);
		this.autoComplete = null;
		if (this.props.hideOnScroll) {
			window.removeEventListener('scroll', this.handleHideOnScroll);
		}
		window.addEventListener('scroll', this.scrollHandler, {
			passive: false,
		});
	}

	onAutocomplete = (value, place) => {
		this.setState({ isFocused: false });
		const locationParams = formatPlaceObject(place);
		this.props.onChange({
			...locationParams,
			location: value,
		});
	};

	// we can't test this because it requires the actual google autocomplete to load in the DOM
	/* istanbul ignore next */
	handleHideOnScroll = (e) => {
		e.preventDefault();
		const GoogleAutocompleteDrawerEl =
			document.querySelector('.pac-container');
		if (
			GoogleAutocompleteDrawerEl &&
			GoogleAutocompleteDrawerEl.style.display !== 'none' &&
			this.textInput === document.activeElement
		) {
			GoogleAutocompleteDrawerEl.style.display = 'none';
		}
	};

	// stripped out browser location detection in favor of cloudflare location in redux store  as part of sf_15652_geolocation_ssdc
	onGetLocation = (geolocation) => {
		this.setState({ locating: true });
		if (geolocation?.city && geolocation?.region && geolocation?.country) {
			const location =
				geolocation.city +
				' ' +
				geolocation.region +
				', ' +
				geolocation.country;
			this.props.onChange({ location });
			this.setState({
				isFocused: false,
				locating: false,
				errorMessage: false,
			});
		} else {
			this.setState({
				isFocused: false,
				locating: false,
				errorMessage: true,
			});
		}
	};

	onChange = ({ target }) => {
		const { value } = target;
		this.props.onChange({ location: value });
	};

	onFocus = () => {
		if (!this.autoComplete) {
			createAutocomplete(this.textInput, this.onAutocomplete).then(
				(autoComplete) => {
					this.autoComplete = autoComplete;
				},
			);
		}
		this.setState({
			isFocused: true,
			errorMessage: false,
		});
	};

	onBlur = () => {
		setTimeout(() => this.setState({ isFocused: false }), 250);
	};

	bindRef = (ref) => {
		const { getRef } = this.props;
		this.textInput = ref;

		if (getRef) {
			getRef(ref);
		}
	};

	render() {
		const { isFocused, locating, errorMessage } = this.state;
		const {
			className,
			label,
			id,
			name,
			placeholder,
			useBrowserLocation,
			value,
			geolocation,
			sf15652Variation,
		} = this.props;

		const sf15652Variation1 = sf15652Variation === 'variation1';

		return (
			<div
				className={classnames({
					[`${className}-wrapper`]: className,
				})}
			>
				<Input
					id={id}
					autoComplete="off"
					name={name}
					label={label}
					className={className}
					placeholder={placeholder}
					getRef={this.bindRef}
					onFocus={this.onFocus}
					onBlur={this.onBlur}
					onChange={this.onChange}
					value={value}
				/>
				{useBrowserLocation && isFocused && sf15652Variation1 && (
					<BodyClassName>
						<Button
							className="use-your-location"
							onClick={() => this.onGetLocation(geolocation)}
						>
							<MapPin fill="black" />
							<Text className="text">Use My Location</Text>
						</Button>
					</BodyClassName>
				)}
				{errorMessage && (
					<div className="error-message">
						<Image
							src={alert}
							alt="Info"
							className="alert-icon"
						/>
						<Paragraph last>
							{
								'We weren’t able to get your location. Please type it in manually.'
							}
						</Paragraph>
					</div>
				)}
				{locating && <LoadingMask modal />}
			</div>
		);
	}
}

GoogleAutocomplete.propTypes = {
	onChange: PropTypes.func.isRequired,
	name: PropTypes.string,
	getRef: PropTypes.func,
	value: PropTypes.string,
	label: PropTypes.string,
	id: PropTypes.string,
	placeholder: PropTypes.string,
	className: PropTypes.string,
	useBrowserLocation: PropTypes.bool,
	hideOnScroll: PropTypes.bool,
	geolocation: PropTypes.object,
	sf15652Variation: PropTypes.string,
};

GoogleAutocomplete.defaultProps = {
	placeholder: 'Enter Zip or City Name',
	label: 'Enter Zip or City Name',
	hideOnScroll: false,
};

export default enhance(GoogleAutocomplete);
